import { useCallback, useEffect, useRef, useState } from "react";
import { fetchRfps } from "../services/rfpsService";
import type { RfpItem, RfpSortBy, RfpSortDir, RfpStatus } from "../types/rfp";

const SEARCH_DEBOUNCE_MS = 400;

export type UseRfpsResult = {
  readonly rfps: readonly RfpItem[];
  readonly loading: boolean;
  readonly error: string | null;
  readonly page: number;
  readonly pageSize: number;
  readonly totalRows: number;
  readonly searchInput: string;
  readonly status: RfpStatus | "";
  readonly sortBy: RfpSortBy;
  readonly sortDir: RfpSortDir;
  readonly setSearchInput: (value: string) => void;
  readonly setStatus: (value: RfpStatus | "") => void;
  readonly setPage: (page: number) => void;
  readonly setPageSize: (pageSize: number) => void;
  readonly setSorting: (sortBy: RfpSortBy, sortDir: RfpSortDir) => void;
  readonly reload: () => void;
};

export function useRfps(): UseRfpsResult {
  const [rfps, setRfps] = useState<RfpItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRows, setTotalRows] = useState(0);
  const [searchInput, setSearchInputState] = useState("");
  const [search, setSearch] = useState("");
  const [status, setStatusState] = useState<RfpStatus | "">("");
  const [sortBy, setSortBy] = useState<RfpSortBy>("created_at");
  const [sortDir, setSortDir] = useState<RfpSortDir>("desc");
  const [reloadToken, setReloadToken] = useState(0);
  const searchTimerRef = useRef<number | null>(null);

  const setSearchInput = useCallback((value: string) => {
    setSearchInputState(value);
    setPage(1);
  }, []);

  const setStatus = useCallback((value: RfpStatus | "") => {
    setStatusState(value);
    setPage(1);
  }, []);

  const updatePageSize = useCallback((nextPageSize: number) => {
    setPageSize(nextPageSize);
    setPage(1);
  }, []);

  const setSorting = useCallback((nextSortBy: RfpSortBy, nextSortDir: RfpSortDir) => {
    setSortBy(nextSortBy);
    setSortDir(nextSortDir);
    setPage(1);
  }, []);

  const reload = useCallback(() => {
    setReloadToken((v) => v + 1);
  }, []);

  useEffect(() => {
    if (searchTimerRef.current) {
      window.clearTimeout(searchTimerRef.current);
    }
    searchTimerRef.current = window.setTimeout(() => {
      setSearch(searchInput.trim());
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      if (searchTimerRef.current) {
        window.clearTimeout(searchTimerRef.current);
      }
    };
  }, [searchInput]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    void (async () => {
      try {
        const response = await fetchRfps({
          page,
          page_size: pageSize,
          search,
          status: status || undefined,
          sort_by: sortBy,
          sort_dir: sortDir,
        });
        if (cancelled) return;
        setRfps(response.data ?? []);
        setTotalRows(response.pagination?.total ?? 0);
      } catch (e) {
        if (cancelled) return;
        const message = e instanceof Error ? e.message : "Failed to load RFPs";
        setError(message);
        setRfps([]);
        setTotalRows(0);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [page, pageSize, search, status, sortBy, sortDir, reloadToken]);

  return {
    rfps,
    loading,
    error,
    page,
    pageSize,
    totalRows,
    searchInput,
    status,
    sortBy,
    sortDir,
    setSearchInput,
    setStatus,
    setPage,
    setPageSize: updatePageSize,
    setSorting,
    reload,
  };
}
